/*
 * Chargements teqaConnect — fichier IDENTIQUE dans teqa_store et
 * teqa_confirmation (src/ui/feedback/Loading.jsx). Modifier les deux ensemble.
 *
 *   <Spinner />                       icône qui tourne (bouton, texte)
 *   <SectionLoader label="…" />       bloc centré (carte, tableau)
 *   <PageLoader />                    plein écran (démarrage, route)
 *   <LoadingOverlay show />           voile sur un conteneur `relative`
 *   <Skeleton className="h-4 w-1/2"/> <TableSkeleton rows={6} cols={5}/> <CardSkeleton/>
 */

const SPIN_SIZES = { xs: "h-3.5 w-3.5", sm: "h-4 w-4", md: "h-6 w-6", lg: "h-10 w-10", xl: "h-14 w-14" };

export function Spinner({ size = "sm", className = "", label }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      className={`${SPIN_SIZES[size] || SPIN_SIZES.sm} shrink-0 animate-spin text-indigo-600 dark:text-indigo-400 ${className}`}
      role={label ? "status" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.18" strokeWidth="3" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

// Compatibilité (ancienne API de teqa_confirmation)
export function LoadingSpinner({ size = "md" }) {
  return <Spinner size={size} label="Chargement" />;
}

export function SectionLoader({ label = "Chargement…", hint, className = "", minHeight = "min-h-[180px]" }) {
  return (
    <div role="status" aria-live="polite" className={`flex ${minHeight} w-full flex-col items-center justify-center gap-3 p-6 text-center ${className}`}>
      <Spinner size="lg" />
      {label ? <p className="text-sm font-medium text-slate-600 dark:text-slate-300">{label}</p> : null}
      {hint ? <p className="max-w-sm text-xs text-slate-400">{hint}</p> : null}
    </div>
  );
}

export function InlineLoader({ label = "Chargement…", className = "" }) {
  return (
    <span role="status" className={`inline-flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 ${className}`}>
      <Spinner size="sm" />
      {label}
    </span>
  );
}

export function PageLoader({ label = "Chargement…" }) {
  return (
    <div role="status" aria-live="polite" className="fixed inset-0 z-[999] flex flex-col items-center justify-center gap-5 bg-slate-50 dark:bg-slate-950">
      <span className="relative flex h-16 w-16 items-center justify-center">
        <span className="absolute inset-0 animate-ping rounded-2xl bg-indigo-500/20" />
        <span className="relative flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-indigo-700 text-lg font-bold text-white shadow-lg">
          tq
        </span>
      </span>
      <InlineLoader label={label} />
    </div>
  );
}

export function LoadingOverlay({ show = true, label = "Chargement…", className = "" }) {
  if (!show) return null;
  return (
    <div role="status" aria-live="polite" className={`absolute inset-0 z-20 flex items-center justify-center rounded-[inherit] bg-white/70 backdrop-blur-[1px] dark:bg-slate-900/70 ${className}`}>
      <span className="rounded-full border border-slate-200 bg-white px-4 py-2 shadow-sm dark:border-slate-700 dark:bg-slate-800">
        <InlineLoader label={label} />
      </span>
    </div>
  );
}

function DotsLoader({ label }) {
  return (
    <div role="status" aria-live="polite" className="flex flex-col items-center gap-3">
      <div className="flex items-center gap-1.5">
        {[0, 1, 2].map((i) => (
          <span key={i} className="h-2.5 w-2.5 animate-bounce rounded-full bg-indigo-500" style={{ animationDelay: `${i * 0.15 - 0.3}s` }} />
        ))}
      </div>
      {label ? <p className="text-sm font-medium text-slate-600 dark:text-slate-300">{label}</p> : null}
    </div>
  );
}

function BarLoader({ label }) {
  return (
    <div role="status" aria-live="polite" className="flex w-full flex-col items-center gap-3">
      <div className="h-1.5 w-full max-w-xs overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
        <div className="h-full w-1/3 animate-pulse rounded-full bg-indigo-500" />
      </div>
      {label ? <p className="text-sm font-medium text-slate-600 dark:text-slate-300">{label}</p> : null}
    </div>
  );
}

/* ----------------------------- Squelettes ----------------------------- */
export function Skeleton({ className = "h-4 w-full", rounded = "rounded-md", style }) {
  return <span aria-hidden="true" style={style} className={`block animate-pulse bg-slate-200/80 dark:bg-slate-800 ${rounded} ${className}`} />;
}

export function SkeletonText({ lines = 3, className = "" }) {
  return (
    <div className={`space-y-2 ${className}`} aria-hidden="true">
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} className={`h-3.5 ${i === lines - 1 ? "w-2/3" : "w-full"}`} />
      ))}
    </div>
  );
}

export function CardSkeleton({ className = "" }) {
  return (
    <div className={`rounded-xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 ${className}`} aria-hidden="true">
      <div className="flex items-center gap-3">
        <Skeleton className="h-10 w-10" rounded="rounded-lg" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-3.5 w-1/2" />
          <Skeleton className="h-3 w-1/3" />
        </div>
      </div>
      <SkeletonText lines={2} className="mt-4" />
    </div>
  );
}

export function StatCardSkeleton() {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900" aria-hidden="true">
      <div className="flex items-center gap-3">
        <Skeleton className="h-10 w-10" rounded="rounded-lg" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-2.5 w-1/3" />
          <Skeleton className="h-5 w-1/2" />
        </div>
      </div>
    </div>
  );
}

export function TableSkeleton({ rows = 6, cols = 5, className = "" }) {
  return (
    <div role="status" aria-label="Chargement" className={`w-full overflow-hidden ${className}`}>
      <div className="flex gap-4 border-b border-slate-100 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-800/60">
        {Array.from({ length: cols }).map((_, c) => (
          <Skeleton key={c} className="h-2.5 flex-1" />
        ))}
      </div>
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex items-center gap-4 border-b border-slate-100 px-4 py-3.5 last:border-0 dark:border-slate-800">
          {Array.from({ length: cols }).map((__, c) => (
            <Skeleton key={c} className={`h-3.5 flex-1 ${c === 0 ? "max-w-[180px]" : ""}`} style={{ opacity: 1 - r * 0.08 }} />
          ))}
        </div>
      ))}
    </div>
  );
}

export function PageSkeleton() {
  return (
    <div className="page-shell space-y-4 p-6" role="status" aria-label="Chargement">
      <Skeleton className="h-7 w-1/3" />
      <div className="grid gap-4 md:grid-cols-3">
        <StatCardSkeleton />
        <StatCardSkeleton />
        <StatCardSkeleton />
      </div>
      <CardSkeleton />
    </div>
  );
}

// Compatibilité (ancienne API de teqa_confirmation)
export function SkeletonLoader({ variant = "card", count = 1 }) {
  const items = Array.from({ length: count });
  if (variant === "card") return <>{items.map((_, i) => <CardSkeleton key={i} />)}</>;
  if (variant === "avatar") return <>{items.map((_, i) => <Skeleton key={i} className="h-10 w-10" rounded="rounded-full" />)}</>;
  if (variant === "title") return <>{items.map((_, i) => <Skeleton key={i} className="h-6 w-1/2" />)}</>;
  if (variant === "button") return <>{items.map((_, i) => <Skeleton key={i} className="h-9 w-28" rounded="rounded-lg" />)}</>;
  return <SkeletonText lines={count} />;
}

/**
 * Composant historique (teqa_store) : <Loading text fullScreen size variant />.
 * variant : "spinner" (défaut) | "dots" | "bar".
 */
export default function Loading({ fullScreen = false, text = "Chargement…", size = "md", variant = "spinner", className = "" }) {
  if (fullScreen) return <PageLoader label={text} />;
  if (variant === "dots") return <div className={`flex justify-center py-6 ${className}`}><DotsLoader label={text} /></div>;
  if (variant === "bar") return <div className={`flex justify-center py-6 ${className}`}><BarLoader label={text} /></div>;
  if (size === "sm") return <InlineLoader label={text} className={className} />;
  return <SectionLoader label={text} className={className} minHeight="min-h-[120px]" />;
}
